import { useQueries, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Settings, Users, Pencil, Trash2, FileText, ChevronRight, Workflow, Lock, Zap, SearchX,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { TagManager } from '@/components/tags/TagManager'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Pagination } from '@/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { getTriggerLabel } from '@/features/automations/triggerLabels'
import { RecordStats } from '@/features/record-groups/components/RecordGroupDetails/RecordStats'
import { RecordFilterBar, type RecordFilterState } from '@/features/records/components/RecordFilterBar'
import { RecordsTable, type RecordSort } from '@/features/records/components/RecordsTable'
import { BoardView } from '@/features/records/components/views/BoardView'
import { CalendarView } from '@/features/records/components/views/CalendarView'
import { GalleryView } from '@/features/records/components/views/GalleryView'
import { INLINE_EDIT_TYPES } from '@/features/records/components/views/InlineCell'
import { TimelineView } from '@/features/records/components/views/TimelineView'
import { ALL_RECORDS, ViewBar } from '@/features/records/components/views/ViewBar'
import { CHOICE_FIELD_TYPES } from '@/features/records/components/views/viewUtils'
import { canEditField, canViewField, isDataField } from '@/features/records/utils/fieldAccess'
import { buildSearchRequest, listedFieldKeys, type RecordFilter } from '@/features/records/utils/recordFilters'
import { useAutomations, useAutomationsForRecordGroup } from '@/hooks/useAutomations'
import { useForm } from '@/hooks/useForm'
import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import { useAllowedActions } from '@/hooks/usePermission'
import { useRecordGroup, useRecordGroupMutations, useRecordGroupSummary } from '@/hooks/useRecordGroups'
import { useRecordViews } from '@/hooks/useRecordViews'
import { useRecordSearch, useRecordSummary } from '@/hooks/useRecords'
import { useUrlState } from '@/hooks/useUrlState'
import { useWorkspace } from '@/hooks/useWorkspace'
import { getForm } from '@/services/api/form.service'
import { exportRecordsCsv, updateRecordValue } from '@/services/api/record.service'
import { useAuthStore } from '@/stores/auth.store'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import type { FormField, Form } from '@/types/api/form'
import type { RecordFilterMatch, RecordListItem, RecordSearchRequest } from '@/types/api/record'
import type { RecordView, RecordViewType } from '@/types/api/record-view'
import { ActionType, AutomationActionType } from '@/types/enums'
import { formatDateTime } from '@/utils/date'
import { parseApiError } from '@/utils/error-parser'

import { NewRecordDialog } from './NewRecordDialog'
import { RecordGroupDashboard } from './RecordGroupDashboard'



// ── automation helpers ────────────────────────────────────────────────────────

function getTriggerBadgeVariant(trigger: string): 'success' | 'default' | 'destructive' | 'secondary' {
  switch (trigger) {
    case ActionType.CREATE: return 'success'
    case ActionType.UPDATE: return 'default'
    case ActionType.DELETE: return 'destructive'
    default:                return 'secondary'
  }
}

function getActionTypeLabel(type: string): string {
  switch (type) {
    case AutomationActionType.ADD_NOTE:      return 'Add Note'
    case AutomationActionType.SEND_EMAIL:    return 'Send Email'
    case AutomationActionType.UPDATE_FIELD:  return 'Update Field'
    case AutomationActionType.SEND_NOTIFICATION: return 'Send Notification'
    default:                                 return type
  }
}

const RECORD_PAGE_SIZES = [10, 25, 50, 100] as const
function SectionHeader({
  title,
  description,
  action,
}: {
  section: 'records' | 'automations' | 'tags' | 'dashboard' | 'settings'
  title: ReactNode
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {action}
    </div>
  )
}
const RECORD_URL_DEFAULTS = {
  q: '',
  match: 'ALL',
  sub: false,
  page: 0,
  size: 25,
  sort: 'createdAt',
  dir: 'DESC',
  tab: 'records',
  /** Active saved view: a view id, or "all" for the built-in list. */
  v: '',
  /** Filter chips as JSON (param "rf"); kept in the same state object so one update writes everything. */
  f: '',
}

function parseFilters(raw: string): RecordFilter[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as RecordFilter[]) : []
  } catch {
    return []
  }
}

/** Record and child-group charts. Mounted only while the Dashboard tab is open, so the other tabs load no summaries. */
function RecordGroupOverview({ recordGroupId }: { recordGroupId: string }) {
  const { data: recordSummary, isLoading } = useRecordSummary(recordGroupId)
  const { data: groupSummary } = useRecordGroupSummary(recordGroupId)
  return (
    <section aria-label="Overview" className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">Overview</h2>
      <RecordStats summary={recordSummary} groupSummary={groupSummary} isLoading={isLoading} />
    </section>
  )
}

export function RecordGroupPage() {
  const { recordGroupId } = useParams<{ recordGroupId: string }>()
  // What the user may do in this group (record-group scoped grants included).
  const groupActions = useAllowedActions('RECORD_GROUP', recordGroupId)
  const canDesignForm = groupActions.can(PERMISSION_KEYS.FORM_CREATE) || groupActions.can(PERMISSION_KEYS.FORM_UPDATE)
  const canCreateRecord = groupActions.can(PERMISSION_KEYS.RECORD_CREATE)
  const canCreateAutomation = groupActions.can(PERMISSION_KEYS.AUTOMATION_CREATE)
  const canToggleAutomations = groupActions.can(PERMISSION_KEYS.AUTOMATION_TOGGLE)
  // Only load what the user may see — a 403 would surface as an error toast.
  const canViewForm = groupActions.can(PERMISSION_KEYS.FORM_VIEW)
  const canViewAutomations = groupActions.can(PERMISSION_KEYS.AUTOMATION_VIEW)
  const navigate = useNavigate()
  const currentUserId = useAuthStore((state) => state.userId)
  const groupMutations = useRecordGroupMutations()
  const { data: recordGroup, isLoading: groupLoading } = useRecordGroup(recordGroupId)
  const { form, isLoading: formLoading } = useForm(recordGroupId!, { enabled: canViewForm })
  const { data: automations = [], isLoading: automationsLoading } =
    useAutomationsForRecordGroup(recordGroupId!, { enabled: canViewAutomations })
  const lockedResourceIds = useSubscriptionStore((state) => state.lockedResourceIds)
  const { toggle: toggleAutomation, isToggling } = useAutomations()

  // Records list state lives in the URL (r* params) so filtered views can be shared and reloaded.
  const [view, setView] = useUrlState('r', RECORD_URL_DEFAULTS)
  const filters = useMemo(() => parseFilters(view.f), [view.f])
  const filterState: RecordFilterState = {
    q: view.q,
    filters,
    match: view.match as RecordFilterMatch,
    includeSubgroups: view.sub,
  }
  const sort: RecordSort = { by: view.sort, dir: view.dir === 'ASC' ? 'ASC' : 'DESC' }

  // ── saved views ────────────────────────────────────────────────────────────
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspace()
  const { views, isLoading: viewsLoading, create: createView, update: updateView, remove: removeView, setDefault: setDefaultView, isSaving: viewsSaving } =
    useRecordViews(recordGroupId)
  const canManageViews = groupActions.can(PERMISSION_KEYS.RECORD_GROUP_UPDATE)
  const canExport = groupActions.can(PERMISSION_KEYS.RECORD_EXPORT)
  const canEditRecords = groupActions.can(PERMISSION_KEYS.RECORD_UPDATE)
  // The user's last choice for this group, used when the URL does not name a view.
  const [storedView, setStoredView] = useLocalStorageState<string>(`recordGroup:${recordGroupId}:view`, '')
  const requestedView = view.v || storedView
  const defaultViewId = recordGroup?.defaultViewId ?? null
  const activeView: RecordView | null =
    views.find((v) => v.id === requestedView) ?? (requestedView === ALL_RECORDS ? null : views.find((v) => v.id === defaultViewId) ?? null)
  const activeId = activeView?.id ?? ALL_RECORDS
  const activeType: RecordViewType = activeView?.type ?? 'LIST'
  const isPagedView = activeType === 'LIST' || activeType === 'TABLE'
  const viewConfig = activeView?.config
  const [columnsOverride, setColumnsOverride] = useState<string[] | null>(null)
  const savedColumns = viewConfig?.columns ?? []
  const columnsNow = columnsOverride ?? savedColumns
  const [calendarMonth, setCalendarMonth] = useState(() => new Date())

  const titleKey = form?.fields.find((f) => f.id === form.mainFieldId)?.key
  const currentDataFields = useMemo(
    () => (form?.fields ?? []).filter((f) => canViewField(f) && isDataField(f) && f.key !== titleKey)
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)),
    [form, titleKey]
  )
  // Field keys the current view needs values for. Sent with the search, so the server loads only these.
  const wantedKeys = useMemo(() => {
    const keys = (() => {
      switch (activeType) {
        case 'TABLE': return columnsNow.length ? columnsNow : currentDataFields.map((f) => f.key)
        case 'BOARD': return [viewConfig?.groupBy]
        case 'CALENDAR': return [viewConfig?.dateField]
        case 'TIMELINE': return [viewConfig?.dateField, viewConfig?.endDateField]
        case 'GALLERY': return currentDataFields.slice(0, 4).map((f) => f.key)
        default: return []
      }
    })()
    return [...new Set(keys.filter((k): k is string => !!k && k !== titleKey))]
  }, [activeType, columnsNow, currentDataFields, viewConfig, titleKey])
  const monthBounds = useMemo(() => {
    const start = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1)
    const end = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
    return [iso(start), iso(end)]
  }, [calendarMonth])
  const searchRequest = useMemo(
    () => buildSearchRequest({
      q: view.q,
      filters,
      match: view.match as RecordFilterMatch,
      includeSubgroups: view.sub,
      currentUserId,
      sort,
      page: isPagedView ? view.page : 0,
      // shortcut: board/calendar/gallery/timeline load one page of 100 records, upgrade to windowed loading when groups outgrow it
      size: isPagedView ? view.size : 100,
    }),
    // sort is derived from view
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, filters, currentUserId, isPagedView]
  )
  const viewRequest = useMemo<RecordSearchRequest>(() => {
    const request: RecordSearchRequest = { ...searchRequest }
    if (wantedKeys.length) request.columns = wantedKeys
    if (activeType === 'CALENDAR' && viewConfig?.dateField) {
      request.fields = [...(request.fields ?? []), { fieldKey: viewConfig.dateField, op: 'BETWEEN', values: monthBounds }]
    }
    return request
  }, [searchRequest, wantedKeys, activeType, viewConfig?.dateField, monthBounds])
  const { data: recordPage, isLoading: recordsLoading, isFetching: recordsFetching } =
    useRecordSearch(recordGroupId, viewRequest)
  const records = useMemo(() => recordPage?.content ?? [], [recordPage])

  const [newRecordOpen, setNewRecordOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editName, setEditName] = useState('')
  // Trace-only: the user can reach this group only because something inside it (or below it) is
  // shared with them; they see the shared records, not the group's settings.
  const isTraceOnly = !!recordGroup?.traceOnly
  const isLocked = lockedResourceIds.has(recordGroupId!)
  const hasChildren = (recordGroup?.childCount ?? 0) > 0

  // Form versions used by the records on this page (per-record-version columns)
  const recordFormIds = useMemo(
    () => Array.from(new Set(records.filter((r) => r.recordGroupId === recordGroupId).map((r) => r.formId))),
    [records, recordGroupId]
  )
  const includedFormIds = useMemo(
    () => (form ? Array.from(new Set([form.id, ...recordFormIds])) : recordFormIds),
    [form, recordFormIds]
  )

  // Memoize queries array to satisfy React hooks rules
  const formQueries = useMemo(
    () =>
      includedFormIds.map((fid) => ({
        queryKey: ['forms', fid],
        queryFn: () => getForm(fid),
        enabled: !!fid && canViewForm,
        staleTime: 60 * 1000,
      })),
    [includedFormIds, canViewForm]
  )

  const formQueriesResults = useQueries({ queries: formQueries })

  // Build forms map
  const formsById = useMemo(() => {
    const map: Record<string, Form> = {}
    formQueriesResults.forEach((q, idx) => {
      if (q.data) map[includedFormIds[idx]] = q.data
    })
    return map
  }, [formQueriesResults, includedFormIds])

  // Build display columns: union of fields across all form versions, ordered by form
  // version (newest first) so the current form's field order always wins for shared
  // keys. Sorting by version explicitly keeps columns for fields that only exist in
  // older form versions from shuffling position between pages.
  const displayColumns = useMemo(() => {
    const orderedFormIds = [...includedFormIds].sort(
      (a, b) => (formsById[b]?.version ?? -1) - (formsById[a]?.version ?? -1)
    )

    const seenKeys = new Set<string>()
    const columns: FormField[] = []
    for (const fid of orderedFormIds) {
      const f = formsById[fid]
      if (!f) continue
      const sorted = [...f.fields].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
      for (const fld of sorted) {
        if (!canViewField(fld) || !isDataField(fld)) continue
        if (!seenKeys.has(fld.key)) {
          seenKeys.add(fld.key)
          columns.push(fld)
        }
      }
    }
    return columns
  }, [formsById, includedFormIds])

  // The list shows the title (main field) plus only the fields it is filtered or sorted by.
  const titleField = form?.fields.find((f) => f.id === form.mainFieldId)
  const tableColumns = useMemo(() => {
    const byKey = new Map(displayColumns.map((c) => [c.key, c]))
    return listedFieldKeys(filters, view.sort)
      .filter((key) => key !== titleField?.key)
      .map((key) => byKey.get(key))
      .filter((c): c is FormField => !!c)
  }, [displayColumns, filters, view.sort, titleField?.key])

  // Table view: the chosen columns (or every field) in order; List view keeps the filtered/sorted ones only.
  const spreadsheetColumns = useMemo(() => {
    const byKey = new Map(displayColumns.map((c) => [c.key, c]))
    return wantedKeys.map((key) => byKey.get(key)).filter((c): c is FormField => !!c)
  }, [displayColumns, wantedKeys])
  const fieldByKey = (key: string | undefined) => displayColumns.find((c) => c.key === key)

  const applyView = (next: RecordView | null) => {
    const c = next?.config ?? {}
    const id = next?.id ?? ALL_RECORDS
    setView({
      v: id, q: c.q ?? '', match: c.match ?? 'ALL', sub: c.sub ?? false,
      f: c.filters?.length ? JSON.stringify(c.filters) : '', sort: c.sort ?? 'createdAt', dir: c.dir ?? 'DESC', page: 0,
    })
    setStoredView(id)
    setColumnsOverride(null)
  }

  // Open the group on its remembered or default view, unless the link already carries filters.
  const appliedInitialView = useRef(false)
  useEffect(() => {
    if (appliedInitialView.current || viewsLoading || !recordGroup) return
    appliedInitialView.current = true
    if (activeView && !view.q && !view.f) applyView(activeView)
    // runs once per page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewsLoading, recordGroup])

  const currentConfig = (extra?: Partial<NonNullable<typeof viewConfig>>) => ({
    q: view.q || undefined,
    match: view.match as RecordFilterMatch,
    sub: view.sub || undefined,
    filters: filters.length ? filters : undefined,
    sort: view.sort,
    dir: view.dir as 'ASC' | 'DESC',
    columns: columnsNow.length ? columnsNow : undefined,
    ...extra,
  })
  const viewDirty = !!activeView && (
    view.q !== (viewConfig?.q ?? '') ||
    view.match !== (viewConfig?.match ?? 'ALL') ||
    view.sub !== !!viewConfig?.sub ||
    view.f !== (viewConfig?.filters?.length ? JSON.stringify(viewConfig.filters) : '') ||
    view.sort !== (viewConfig?.sort ?? 'createdAt') ||
    view.dir !== (viewConfig?.dir ?? 'DESC') ||
    JSON.stringify(columnsNow) !== JSON.stringify(savedColumns)
  )

  const canEditCell = (record: RecordListItem, column: FormField) =>
    canEditRecords && canEditField(column) && INLINE_EDIT_TYPES.has(column.type) &&
    (record.values.some((v) => v.formFieldKey === column.key) || record.formId === column.formId)
  const handleCellEdit = async (record: RecordListItem, column: FormField, value: string) => {
    const existing = record.values.find((v) => v.formFieldKey === column.key)
    try {
      await updateRecordValue(record.recordGroupId, existing
        ? { fieldKey: column.key, fieldLabel: column.label, valueId: existing.valueId, value }
        : { fieldKey: column.key, fieldLabel: column.label, recordId: record.recordId, formFieldId: column.id, value })
      await queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId, 'records'] })
    } catch (error) {
      toast.error(parseApiError(error).detail || 'Update failed')
      throw error
    }
  }
  const handleExport = async () => {
    try {
      const blob = await exportRecordsCsv(recordGroupId!, { ...viewRequest, includeSubgroups: undefined, page: 0, size: 100 })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${recordGroup?.name ?? 'records'}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(parseApiError(error).detail || 'Export failed')
    }
  }

  // Early returns must be AFTER all hooks
  if (groupLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-36 w-full rounded-xl" />
      </div>
    )
  }
  if (!recordGroup) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-medium">Record Group not found</h2>
          <Button variant="link" onClick={() => navigate('/record-groups')}>
            Back to Record Groups
          </Button>
        </div>
      </div>
    )
  }

  const handleNewRecord = () => {
    if (isLocked) return
    if (!form) {
      navigate(`/record-groups/${recordGroupId}/form`)
      return
    }
    setNewRecordOpen(true)
  }

  const handleAutomationToggle = (id: string, enabled: boolean) => {
    if (isLocked) {
      toast.warning('This record group is locked. Upgrade your subscription to enable automations.')
      return
    }
    toggleAutomation(
      { id, enabled: !enabled },
      { onError: () => toast.error('Failed to update automation') }
    )
  }

  const updateFilters = (patch: Partial<RecordFilterState>) => {
    // One URL update: consecutive setSearchParams calls in the same tick overwrite each other.
    const urlPatch: Partial<typeof RECORD_URL_DEFAULTS> = { page: 0 }
    if (patch.filters !== undefined) urlPatch.f = patch.filters.length ? JSON.stringify(patch.filters) : ''
    if (patch.q !== undefined) urlPatch.q = patch.q
    if (patch.match !== undefined) urlPatch.match = patch.match
    if (patch.includeSubgroups !== undefined) urlPatch.sub = patch.includeSubgroups
    setView(urlPatch)
  }

  const filtersActive = !!view.q || filters.length > 0
  const totalRecords = recordPage?.totalElements ?? 0
  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs value={view.tab}>

        <TabsContent value="records" className="mt-4 space-y-3">
          <SectionHeader
            section="records"
            title={isLocked ? <span className="flex items-center gap-2">Records <Badge variant="warning">Locked</Badge></span> : 'Records'}
            description="Search, filter and open the records in this group."
            action={canCreateRecord && (form || canDesignForm) && (
              <Button onClick={handleNewRecord} disabled={isLocked}>
                <Plus className="mr-2 h-4 w-4" />
                New Record
              </Button>
            )}
          />
          {form && (
            <ViewBar
              views={views}
              activeId={activeId}
              defaultViewId={defaultViewId}
              currentUserId={currentUserId}
              canManageShared={canManageViews}
              canExport={canExport}
              dirty={viewDirty}
              fields={displayColumns}
              columns={columnsNow}
              onColumnsChange={setColumnsOverride}
              activeType={activeType}
              saving={viewsSaving}
              onSelect={(id) => applyView(views.find((v) => v.id === id) ?? null)}
              onCreate={async (name, type, visibility, extra) => {
                const created = await createView({ name, type, visibility, config: currentConfig(extra) })
                setView({ v: created.id })
                setStoredView(created.id)
                setColumnsOverride(null)
              }}
              onEdit={async (target, name, type, visibility, extra) => {
                await updateView({ id: target.id, data: { name, type, visibility, config: { ...target.config, ...extra } } })
              }}
              onSaveCurrent={async () => {
                if (!activeView) return
                await updateView({ id: activeView.id, data: { name: activeView.name, type: activeView.type, visibility: activeView.visibility, config: { ...activeView.config, ...currentConfig() } } })
                setColumnsOverride(null)
                toast.success('View saved')
              }}
              onDelete={async (target) => { await removeView(target.id); applyView(null) }}
              onSetDefault={async (id) => { await setDefaultView(id); toast.success('Default view updated') }}
              onExport={() => void handleExport()}
            />
          )}
          {(form || totalRecords > 0 || filtersActive) && (
            <RecordFilterBar
              recordGroupId={recordGroupId!}
              fields={displayColumns}
              state={filterState}
              onChange={updateFilters}
              hasSubgroups={hasChildren}
              sort={sort}
              onSortChange={(next) => setView({ sort: next.by, dir: next.dir, page: 0 })}
            />
          )}
          {formLoading || recordsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : records.length === 0 && filtersActive ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <SearchX className="h-8 w-8 text-muted-foreground" />
                <h3 className="mt-3 text-base font-semibold text-foreground">No records match these filters</h3>
                <Button variant="link" onClick={() => updateFilters({ q: '', filters: [], match: 'ALL' })}>
                  Clear filters
                </Button>
              </CardContent>
            </Card>
          ) : isTraceOnly && !form ? (
            /* Trace-only without form access: list the records shared with the user by title */
            records.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-sm text-neutral-500">
                  Nothing in this group is shared with you directly.
                </CardContent>
              </Card>
            ) : (
              <Card className="divide-y divide-neutral-100">
                {records.map((record) => (
                  <Link
                    key={record.recordId}
                    to={`/record-groups/${record.recordGroupId}/records/${record.recordId}`}
                    className="flex items-center justify-between px-4 py-3 text-sm hover:bg-neutral-50"
                  >
                    <span className="font-medium text-neutral-800">{record.title || 'Untitled record'}</span>
                    <span className="flex items-center gap-3 text-neutral-400">
                      {formatDateTime(record.createdAt)}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </Card>
            )
          ) : !form && records.length === 0 ? (
            /* No form configured — prompt user to go to designer */
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-14">
                <div className="rounded-2xl bg-neutral-100 p-4">
                  <FileText className="h-10 w-10 text-neutral-400" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-neutral-700">No form configured</h3>
                <p className="mt-1.5 max-w-xs text-center text-sm text-neutral-500">
                  Design a form for this record group before you can create records.
                </p>
                {canDesignForm && (
                  <Button className="mt-5" asChild>
                    <Link to={`/record-groups/${recordGroupId}/form`}>
                      <Settings className="mr-2 h-4 w-4" />
                      Open Form Designer
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : records.length === 0 ? (
            /* Form exists but no records yet */
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-14">
                <div className="rounded-2xl bg-neutral-100 p-4">
                  <Users className="h-10 w-10 text-neutral-400" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-neutral-700">No records yet</h3>
                <p className="mt-1.5 text-sm text-neutral-500">
                  Create your first record in this group.
                </p>
                {canCreateRecord && (
                  <Button className="mt-5" onClick={() => setNewRecordOpen(true)} disabled={isLocked}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Record
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : isPagedView ? (
            <RecordsTable
              records={records}
              titleLabel={titleField?.label ?? 'Title'}
              columns={activeType === 'TABLE' ? spreadsheetColumns : tableColumns}
              sort={sort}
              onSortChange={(next) => setView({ sort: next.by, dir: next.dir, page: 0 })}
              busy={recordsFetching}
              onCellEdit={activeType === 'TABLE' ? handleCellEdit : undefined}
              canEditCell={activeType === 'TABLE' ? canEditCell : undefined}
            />
          ) : activeType === 'BOARD' ? (
            <BoardView
              records={records}
              groupBy={CHOICE_FIELD_TYPES.has(fieldByKey(viewConfig?.groupBy)?.type ?? '') ? fieldByKey(viewConfig?.groupBy) : undefined}
              onMove={handleCellEdit}
              canMove={canEditCell}
            />
          ) : activeType === 'CALENDAR' ? (
            <CalendarView records={records} dateField={fieldByKey(viewConfig?.dateField)} month={calendarMonth} onMonthChange={setCalendarMonth} />
          ) : activeType === 'GALLERY' ? (
            <GalleryView records={records} fields={currentDataFields} />
          ) : (
            <TimelineView records={records} startField={fieldByKey(viewConfig?.dateField)} endField={fieldByKey(viewConfig?.endDateField)} />
          )}
          {!isPagedView && recordPage && recordPage.totalElements > records.length && (
            <p className="text-center text-sm text-muted-foreground">
              Showing the first {records.length} of {recordPage.totalElements} records. Narrow the filters to see the rest.
            </p>
          )}
          {isPagedView && recordPage && records.length > 0 && (
            <Pagination
              page={recordPage.page}
              size={view.size}
              totalElements={recordPage.totalElements}
              totalPages={recordPage.totalPages}
              onPageChange={(page) => setView({ page })}
              onSizeChange={(size) => setView({ size, page: 0 })}
              sizeOptions={RECORD_PAGE_SIZES}
              itemLabel="records"
            />
          )}
        </TabsContent>

        <TabsContent value="automations" className="mt-4 space-y-3">
          <SectionHeader
            section="automations"
            title="Automations"
            description="Run actions automatically when records in this group change."
            action={canCreateAutomation && (
              <Button asChild>
                <Link to={`/automations/new?recordGroupId=${recordGroupId}`}>
                  <Plus className="mr-2 h-4 w-4" />
                  New automation
                </Link>
              </Button>
            )}
          />
          <TooltipProvider>
            {automationsLoading ? (
              <div className="grid gap-4 md:grid-cols-3">
                {[1, 2].map((i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-5 w-1/2" />
                      <Skeleton className="h-4 w-1/3" />
                    </CardHeader>
                    <CardContent><Skeleton className="h-4 w-full" /></CardContent>
                  </Card>
                ))}
              </div>
            ) : automations.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-14">
                  <div className="rounded-2xl bg-neutral-100 p-4">
                    <Workflow className="h-10 w-10 text-neutral-400" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-neutral-700">No automations yet</h3>
                  <p className="mt-1.5 text-sm text-neutral-500">
                    Create an automation to trigger actions on record events.
                  </p>
                  {canCreateAutomation && (
                    <Button className="mt-5" asChild>
                      <Link to={`/automations/new?recordGroupId=${recordGroupId}`}>
                        <Plus className="mr-2 h-4 w-4" />
                        New Automation
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-3">
                {automations.map((automation) => (
                  <Card
                    key={automation.id}
                    className={`relative cursor-pointer transition-shadow hover:shadow-md
                      ${isLocked ? 'border-amber-200 bg-amber-50/30 opacity-75' : ''}`}
                    onClick={() => navigate(`/automations/${automation.id}`)}
                  >
                    {/* Locked banner */}
                    {isLocked && (
                      <div className="absolute inset-x-0 top-0 flex items-center gap-1.5 rounded-t-lg bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                        <Lock className="h-3 w-3" />
                        Record group locked — automation disabled
                      </div>
                    )}

                    <CardHeader className={`pb-2 ${isLocked ? 'pt-8' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1.5">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <Badge variant={getTriggerBadgeVariant(automation.triggerEvent) as 'success' | 'default' | 'destructive' | 'secondary'}>
                              {getTriggerLabel(automation.triggerEvent)}
                            </Badge>
                            {isLocked && <Lock className="h-3.5 w-3.5 text-amber-500" />}
                          </CardTitle>
                          <CardDescription>
                            {automation.actions.length} action{automation.actions.length !== 1 ? 's' : ''}
                          </CardDescription>
                        </div>

                        <div
                          className="flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isLocked ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Switch checked={false} disabled className="cursor-not-allowed" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Unlock the record group to enable this automation
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Switch
                              checked={automation.enabled}
                              onCheckedChange={() => handleAutomationToggle(automation.id, automation.enabled)}
                              disabled={isToggling || !canToggleAutomations}
                            />
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent>
                      <div className="flex flex-wrap gap-1.5">
                        {automation.actions.map((action, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700"
                          >
                            <Zap className="h-3 w-3 text-neutral-400" />
                            {getActionTypeLabel(action.type)}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TooltipProvider>
        </TabsContent>

        {/* Phase 5 — Tags tab */}
        <TabsContent value="tags" className="mt-4 space-y-3">
          <SectionHeader
            section="tags"
            title="Tags"
            description="Organise records with tag categories and a default tag for new records."
          />
          <TagManager
            recordGroupId={recordGroupId!}
            readOnly={!groupActions.can(PERMISSION_KEYS.TAG_MANAGE)}
            defaultRecordTagId={recordGroup.defaultRecordTagId ?? null}
            onDefaultRecordTagChange={(tagId) => groupMutations.setDefaultRecordTag(
              { id: recordGroupId!, tagId },
              {
                onSuccess: () => toast.success(tagId ? 'Default tag updated' : 'Default tag removed'),
                onError: () => toast.error('Failed to update the default tag'),
              }
            )}
          />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4 space-y-6">
          <SectionHeader
            section="dashboard"
            title="Dashboard"
            description="Charts and widgets for the records in this group."
          />
          <RecordGroupOverview recordGroupId={recordGroupId!} />
          <RecordGroupDashboard recordGroupId={recordGroupId!} />
        </TabsContent>

        <TabsContent value="settings" className="mt-4 space-y-3">
          <SectionHeader
            section="settings"
            title="Settings"
            description="Rename this record group or move it to trash."
          />
          <Card>
            <CardHeader>
              <CardTitle>Record Group Settings</CardTitle>
              <CardDescription>Configure this record group's settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <span className="text-sm font-medium text-neutral-500">Name</span>
                <p className="text-sm">{recordGroup.name}</p>
              </div>
              {canManageViews && (
                <div className="space-y-1.5">
                  <span className="text-sm font-medium text-neutral-500">Default records view</span>
                  <p className="text-xs text-muted-foreground">Opens first for everyone; people can still switch views or keep their own choice.</p>
                  <Select
                    value={defaultViewId ?? ALL_RECORDS}
                    onValueChange={(id) => void setDefaultView(id === ALL_RECORDS ? null : id).then(() => toast.success('Default view updated'))}
                  >
                    <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_RECORDS}>All records (list)</SelectItem>
                      {views.filter((v) => v.visibility === 'SHARED').map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                {groupActions.can(PERMISSION_KEYS.RECORD_GROUP_UPDATE) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditName(recordGroup.name)
                      setEditDialogOpen(true)
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                )}
                {groupActions.can(PERMISSION_KEYS.RECORD_GROUP_DELETE) && (
                  <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Move to Trash
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Record Dialog */}
      {newRecordOpen && (
        <NewRecordDialog
          open={newRecordOpen}
          onClose={() => setNewRecordOpen(false)}
          recordGroupId={recordGroupId!}
        />
      )}

      {/* Edit Record Group Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Record Group</DialogTitle>
            <DialogDescription>Update the name of this record group.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label htmlFor="edit-rg-name" className="mb-1.5 block text-sm font-medium">
              Name
            </label>
            <input
              id="edit-rg-name"
              className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-900"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editName.trim()) {
                  e.preventDefault()
                  groupMutations.update(
                    { id: recordGroupId!, data: { name: editName.trim(), parentRecordGroupId: recordGroup.parentRecordGroupId } },
                    {
                      onSuccess: () => {
                        toast.success('Record group updated')
                        setEditDialogOpen(false)
                      },
                      onError: (error) => {
                        toast.error(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`)
                      },
                    }
                  )
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!editName.trim() || editName.trim() === recordGroup.name || groupMutations.isUpdating}
              onClick={() => {
                groupMutations.update(
                  { id: recordGroupId!, data: { name: editName.trim(), parentRecordGroupId: recordGroup.parentRecordGroupId } },
                  {
                    onSuccess: () => {
                      toast.success('Record group updated')
                      setEditDialogOpen(false)
                    },
                    onError: (error) => {
                      toast.error(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`)
                    },
                  }
                )
              }}
            >
              {groupMutations.isUpdating ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete to Trash Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Trash?</DialogTitle>
            <DialogDescription>This record group will be moved to trash and can be restored within 30 days.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={groupMutations.isDeleting}
              onClick={() => {
                groupMutations.delete(recordGroupId!, {
                  onSuccess: () => {
                    toast.success('Record group moved to trash')
                    setDeleteDialogOpen(false)
                    navigate('/record-groups')
                  },
                  onError: (error) => {
                    toast.error(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`)
                  },
                })
              }}
            >
              Move to Trash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
